import { Button, ButtonGroup, Center, useToast } from "@chakra-ui/react";
import { useFormik } from "formik";
import { useState } from "react";
import { useCookies } from "react-cookie";
import { Link, useLocation, useNavigate } from "react-router";
import * as Yup from "yup";

import { AttemptsWarning } from "@equidefi/portals/components/AttemptsWarning";
import { SystemDownBanner } from "@equidefi/portals/components/SystemDownBanner";
import { useLogin, useWorkflowLogin } from "@equidefi/portals/hooks/useAuthentication";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { investmentBcode, offeringInactiveWarning, offeringIsActive, offeringIsTerminated } from "@equidefi/shared";
import { BannerAlert, Input, Password, Text } from "@equidefi/ui";

import { PoweredBySection } from "@/components/nav/components";
import { OfferingInfoCard } from "@/components/workflow/OfferingInfoCard";
import { WorkflowButton } from "@/components/workflow/WorkflowButton";

import { WorkflowContainer } from "../WorkflowContainer";
import WorkflowForm from "../WorkflowForm";
import { useWorkflowContext } from "../context/WorkflowContext";
import "./auth.css";

const userSchema = Yup.object().shape({
  email: Yup.string().nullable().email("Must be a valid email").required("Please enter your email address"),
  password: Yup.string().required("Please enter a password"),
});

const initialValues = {
  email: "",
  password: "",
};

const Login = () => {
  const location = useLocation();
  const { offering } = useWorkflowContext();
  const workflowLogin = useWorkflowLogin(offering?.id);
  const login = useLogin();
  const navigate = useNavigate();
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [cookies] = useCookies(["bcode", `ref_${offering?.slug}`]);
  useDocumentTitle("Log In");

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      if (offeringIsTerminated(offering)) {
        await login.mutateAsync({
          email: data.email,
          password: data.password,
        });
      } else {
        await workflowLogin.mutateAsync({
          email: data.email,
          password: data.password,
          bcode: investmentBcode(cookies?.bcode),
          ref: cookies?.[`ref_${offering?.slug}`],
          type: "investor",
        });
      }
      navigate(`/offerings/${offering.slug}/mfa`, { state: data });
    } catch (error) {
      console.error(error);
      toast({
        status: "error",
        description: error?.response?.data?.errors.join(". ") || "There was an error trying to login.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const { handleSubmit, getFieldProps, errors, isValid, touched, values } = useFormik({
    initialValues: location?.state?.initialValues || initialValues,
    enableReinitialize: true,
    validationSchema: userSchema,
    onSubmit,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
  });

  const isOfferingNotActive = offering?.status && !offeringIsActive(offering);
  const inactiveWarning = offeringInactiveWarning(offering);

  return (
    <WorkflowContainer title={`Welcome to ${offering.issuer.name}'s offering!`}>
      <SystemDownBanner />
      <OfferingInfoCard offering={offering} />
      <Text mb="0">Thank you for your interest in {offering.issuer.name}.</Text>
      <Text mb={4}>Register or Log In to start your investment.</Text>

      {inactiveWarning && <BannerAlert status="warning" mb={4} description={inactiveWarning} />}

      <ButtonGroup w="full" variant="solid" mb="4">
        <Button
          w="full"
          as={Link}
          to={"../registration"}
          state={{ initialValues: { ...values, password: "" } }}
          boxShadow="card"
          colorScheme="gray"
          bgColor="white"
          minH={{ base: "48px", md: "inherit" }}
          color="black">
          REGISTER
        </Button>
        <Button
          w="full"
          backgroundColor="purple.100"
          boxShadow="card"
          border="1px solid"
          borderColor="purple.500"
          color="black"
          fontWeight="bold"
          _hover={{}}
          _active={{}}
          minH={{ base: "48px", md: "inherit" }}
          cursor="none">
          LOG IN
        </Button>
      </ButtonGroup>

      <AttemptsWarning error={login.error ?? workflowLogin.error} mb={4} />

      <WorkflowForm onSubmit={handleSubmit}>
        <Input
          label="Email Address"
          error={errors.email}
          isInvalid={errors.email && touched.email}
          isRequired
          isDisabled={isOfferingNotActive}
          mb={4}
          {...getFieldProps("email")}
        />
        <Password
          label="Password"
          error={errors.password}
          isInvalid={errors.password && touched.password}
          isRequired
          isDisabled={isOfferingNotActive}
          borderRadius={5}
          {...getFieldProps("password")}
        />
      </WorkflowForm>
      <WorkflowButton isDisabled={!isValid || isOfferingNotActive} isLoading={isLoading} onClick={handleSubmit}>
        Log In
      </WorkflowButton>
      <Center mt="4">
        <Text
          as={Link}
          _hover={{ color: "blue.700" }}
          color="blue.500"
          fontSize="md"
          to="/forgot"
          state={{
            redirectUrl: `/offerings/${offering.slug}/login`,
          }}>
          Forgot Password?
        </Text>
      </Center>
      <PoweredBySection variant="light" />
    </WorkflowContainer>
  );
};

export default Login;
